/**
 * Inhalte des geführten Rundgangs im Kundenportal.
 *
 * Gleiche Bauart wie der Techniker-Rundgang (`tourSteps.ts`): routen-basiert statt an
 * CSS-Selektoren aufgehängt, damit er nicht bei der nächsten Layout-Änderung still
 * bricht. Der Ton ist ein anderer — hier liest ein Kunde mit, kein Kollege.
 *
 * Grundregel für die Texte: nur versprechen, was das Portal wirklich tut. Ein Kunde,
 * der einmal etwas erwartet hat, das nicht kommt, glaubt der Anleitung danach nichts
 * mehr.
 */

import type { TourStep } from './tourSteps';

export const CUSTOMER_TOUR_STEPS: TourStep[] = [
  {
    id: 'dashboard',
    path: '/customer/dashboard',
    title: 'Ihr Überblick',
    body:
      'Das Dashboard fasst zusammen, was gerade läuft: offene Tickets, anstehende Termine ' +
      'und neue Nachrichten. Die drei Kacheln oben führen mit einem Klick in den jeweiligen ' +
      'Bereich, darunter liegt der Schnellstart für die zwei häufigsten Wege.',
    steps: [
      '„Neue Anfrage" — der übliche Weg, uns etwas zu melden.',
      '„Meine Tickets" — alles, was gerade läuft.',
      'Roter Kasten darunter: der direkte Weg zur Notfallmeldung.',
    ],
  },
  {
    id: 'tickets',
    path: '/customer/tickets',
    module: 'tickets',
    title: 'Tickets — Ihre Anfragen im Verlauf',
    body:
      'Alle laufenden und abgeschlossenen Anfragen mit aktuellem Status. Ein Klick öffnet ' +
      'den Vorgang — dort steckt deutlich mehr als der Verlauf: Sie können nachhaken, ' +
      'Dateien anhängen, einen Wunschtermin nennen und das Ticket selbst schließen.',
    steps: [
      '„An das Ticket erinnern" — bittet um Priorisierung, das Team wird benachrichtigt.',
      '„Nach Status fragen" — fragt kurz den aktuellen Stand ab.',
      'Kommentieren und Fotos anhängen: beides im Feld unten am Ticket.',
      'Wunschtermin eintragen und den Titel nachschärfen, solange das Ticket offen ist.',
      '„Ticket schließen", wenn sich etwas von selbst erledigt hat.',
    ],
    caution:
      'Meldet das Team eine Lösung, erscheint oben „Ja, gelöst" bzw. „Nein, besteht weiter". ' +
      'Die Rückmeldung hilft uns — und öffnet das Ticket bei Bedarf sofort wieder.',
  },
  {
    id: 'requests',
    path: '/customer/requests',
    module: 'requests',
    title: 'Neue Anfrage stellen',
    body:
      'Der übliche Weg, uns etwas zu melden. Sie wählen eine Kategorie und füllen das ' +
      'passende Formular aus — dadurch landen die Angaben gleich vollständig beim richtigen ' +
      'Team, statt in einer Rückfrage-Schleife.',
    caution:
      'Für Störungen mit hoher Betriebswirkung gibt es den Notfall-Weg — siehe nächste Station.',
  },
  {
    id: 'emergency',
    path: '/customer/emergency',
    title: 'Notfall melden',
    body:
      'Für Ausfälle, die den Betrieb spürbar treffen. Eine Meldung über diesen Weg wird ' +
      'umgehend priorisiert bearbeitet.',
    notFor:
      'Für alles Planbare — neue Geräte, Fragen, kleinere Störungen — nutzen Sie die normale ' +
      'Anfrage. Der Notfall-Weg bleibt so für echte Notfälle frei.',
  },
  {
    id: 'inbox',
    path: '/customer/inbox',
    module: 'inbox',
    title: 'Postfach',
    body:
      'Nachrichten von FixByte an Sie — Rückfragen, Hinweise, Statusmeldungen. Ungelesenes ' +
      'ist markiert, damit nichts untergeht.',
  },
  {
    id: 'quotes',
    path: '/customer/quotes',
    module: 'quotes',
    title: 'Angebote',
    comingSoon: true,
    body:
      'Künftig sehen Sie hier Angebote von FixByte und können sie direkt freigeben oder ' +
      'ablehnen, ohne Hin und Her per Mail. Bis zur Freischaltung läuft das wie gewohnt ' +
      'über Ihren Ansprechpartner.',
  },
  {
    id: 'shop',
    path: '/customer/shop',
    module: 'shop',
    title: 'Shop',
    comingSoon: true,
    body:
      'Künftig können Sie hier häufig benötigte Artikel und Leistungen direkt bestellen. ' +
      'Bis dahin nutzen Sie bitte eine normale Anfrage — dort ist die Kategorie ' +
      '„Bestellung" vorgesehen.',
  },
  {
    id: 'submissions',
    path: '/customer/submissions',
    module: 'submissions',
    title: 'Serviceberichte',
    body:
      'Abgeschlossene Serviceeinsätze erscheinen hier als PDF zum Herunterladen — mit dem, ' +
      'was gemacht wurde, und der Unterschrift von der Abnahme.',
    steps: ['Bericht zum Ablegen oder Weiterleiten einfach als PDF herunterladen.'],
  },
  {
    id: 'assets',
    path: '/customer/assets',
    module: 'assets',
    title: 'Assets & Geräte',
    comingSoon: true,
    body:
      'Künftig finden Sie hier Ihre bei uns erfassten Geräte mit Seriennummer, Garantiestand ' +
      'und Service-Historie. Solange der Bereich nicht freigeschaltet ist, fragen Sie bei ' +
      'Bedarf einfach nach — wir haben die Daten.',
  },
  {
    id: 'contracts',
    path: '/customer/contracts',
    module: 'contracts',
    title: 'Verträge & Lizenzen',
    body:
      'Alle Lizenzen, Server, Domains und Verträge, die FixByte für Sie bereitstellt oder ' +
      'verwaltet — mit Laufzeit, Kündigungsfrist und dem Datum, bis wann Sie handeln müssten. ' +
      'Oben steht die nächste Frist, darunter alle Posten; ein Klick öffnet die Details.',
    steps: [
      'Das Abzeichen am Posten zeigt, wer Vertragspartner ist: „von FixByte bereitgestellt" oder „dein Vertrag · von FixByte verwaltet".',
      'Im Detail: „Änderung", „Kündigung", „Verlängern" oder „Frage" — daraus entsteht direkt eine Anfrage bei uns.',
      '„Kalender" übernimmt alle Fristen als Abo in Outlook, Google oder Apple Kalender.',
    ],
    caution:
      'Verlängert sich ein Posten nicht von selbst, brauchen wir vor Ablauf Ihre Entscheidung — ' +
      'der Status ist dann orange oder rot.',
  },
  {
    id: 'appointments',
    path: '/customer/appointments',
    module: 'appointments',
    title: 'Termine',
    body:
      'Geplante Service-Einsätze mit Datum und Uhrzeit. Einzelne Termine lassen sich ' +
      'herunterladen — oder Sie abonnieren den Kalender einmalig, dann erscheinen neue ' +
      'Termine automatisch in Outlook oder Apple Kalender.',
    steps: [
      '„Kalender abonnieren" liefert einen Link zum einmaligen Einrichten.',
      'Einzelne Termine per Download in den eigenen Kalender übernehmen.',
    ],
  },
  {
    id: 'trust',
    path: '/customer/trust',
    module: 'trust',
    title: 'Trust Center',
    comingSoon: true,
    body:
      'Künftig legen wir hier Kennzahlen zur Service-Qualität offen — wie schnell reagiert ' +
      'und gelöst wird.',
  },
  {
    id: 'audit',
    path: '/customer/audit',
    module: 'audit',
    title: 'Protokoll',
    body:
      'Jede Aktivität in Ihrem Portal-Konto wird protokolliert — wer sich angemeldet, was ' +
      'wer geöffnet oder geändert hat. Damit bleibt nachvollziehbar, was in Ihrem Namen ' +
      'geschehen ist.',
  },
  {
    id: 'settings',
    path: '/customer/settings',
    title: 'Einstellungen und Zugänge',
    body:
      'Ihre Kontodaten und Benachrichtigungen. Unter „Team" verwalten Sie, wer aus Ihrem ' +
      'Haus Zugang zum Portal hat — Zugänge anlegen und wieder entziehen.',
    steps: [
      'Team: Portal-Zugänge für Kolleginnen und Kollegen freischalten.',
      'Export: Ihre Daten als Datei herunterladen.',
    ],
  },
  {
    id: 'help',
    path: '/customer/help',
    title: 'Wo Sie das hier wiederfinden',
    body:
      'Diese Seite ist Ihr Nachschlagewerk. Den Rundgang starten Sie von hier jederzeit neu, ' +
      'darunter finden Sie die häufigen Fragen und den Weg zum Kontakt.',
    caution:
      'Der Hinweis auf dem Dashboard verschwindet, sobald Sie ihn schließen oder den Rundgang ' +
      'beenden — diese Seite bleibt.',
  },
];

/**
 * Rundgang für die freigeschalteten Bereiche eines Kunden.
 *
 * Gesperrte Bereiche stehen im Menü und brauchen eine Erklärung — aber keinen eigenen
 * Gang durch zehn leere Seiten. Sie werden deshalb zu EINER Station „demnächst"
 * zusammengefasst, direkt vor der Hilfe.
 */
export function buildCustomerTourSteps(modules: readonly string[]): TourStep[] {
  const isOpen = (step: TourStep) => !step.module || modules.includes(step.module);
  const open = CUSTOMER_TOUR_STEPS.filter(isOpen).map((step) =>
    step.id === 'dashboard' ? dashboardStepFor(step, modules) : step
  );
  const locked = CUSTOMER_TOUR_STEPS.filter((step) => !isOpen(step));
  if (locked.length === 0) return open;

  const comingSoon: TourStep = {
    id: 'coming-soon',
    path: locked[0]!.path,
    title: 'Weitere Bereiche — demnächst',
    comingSoon: true,
    body:
      'Im Menü sehen Sie weitere Bereiche mit dem Hinweis „Demnächst". Sie werden für Ihr ' +
      'Unternehmen nach und nach freigeschaltet; bis dahin zeigen sie keine Daten.',
    steps: locked.map((step) => step.title.split(' — ')[0]!),
  };
  const helpIndex = open.findIndex((step) => step.id === 'help');
  return helpIndex >= 0 ? [...open.slice(0, helpIndex), comingSoon, ...open.slice(helpIndex)] : [...open, comingSoon];
}

/** Die Dashboard-Station nennt nur Kacheln und Wege, die es für diesen Kunden gibt. */
function dashboardStepFor(step: TourStep, modules: readonly string[]): TourStep {
  const steps = [
    modules.includes('requests') ? '„Neue Anfrage" — der übliche Weg, uns etwas zu melden.' : null,
    modules.includes('tickets') ? '„Meine Tickets" — alles, was gerade läuft.' : null,
    modules.includes('contracts') ? 'Die Karte „Verträge & Lizenzen" zeigt die nächsten Fristen.' : null,
    'Roter Kasten: der direkte Weg zur Notfallmeldung.',
  ].filter((line): line is string => line !== null);
  return {
    ...step,
    body:
      'Das Dashboard fasst zusammen, was für Ihr Unternehmen freigeschaltet ist, und führt mit ' +
      'einem Klick in den jeweiligen Bereich.',
    steps,
  };
}
